import { LetsTalkProps } from "components/LetsTalk/types"

export interface Image {
  src: string
  width: string
}

export interface ImageResponsive {
  large: Image
  alt: string
  small: Image
}

export interface HeroImageData {
  landscape: HeroImageOrientation
  portrait: HeroImageOrientation
}

export interface HeroImageOrientation {
  large: Image
  small: Image
}

export interface FlowState {
  atEndScreen: boolean
  currentQuestionId: FlowQuestionId
  previousQuestionId: FlowQuestionId | null
  nextQuestionId: FlowQuestionId | null
  inProgress: boolean
  outcome: any
  showContact: boolean
  questions: Record<FlowQuestionId, FlowQuestionData>
  userAnswers: any
  redirect: any
}

export interface FlowData {
  flowId: number
  learnMore: Record<string, FlowLearnMoreData>
  nextCallbacks: Record<FlowQuestionId, Function>
  outcomes: FlowOutcomes
  questions: Record<FlowQuestionId, FlowQuestionData>
  scheduleConversation: LetsTalkProps
  start: FlowStartData
  variantId: number
  videos: any
  vocabulary: FlowVocabularyData
}

export enum FlowQuestionId {
  CashOutAmount = "cashOutAmount",
  CashOutIntention = "cashOutIntention",
  CreditScoreIncrease = "creditScoreIncrease",
  End = "end",
  FavoriteGreeting = "favoriteGreeting",
  FullDate = "fullDate",
  IsFirstTime = "isFirstTime",
  IsStillFixedRate = "isStillFixedRate",
  KnowledgeLevel = "knowledgeLevel",
  LoanBalance = "loanBalance",
  LoanRate = "loanRate",
  LoanYear = "loanYear",
  MainGoal = "mainGoal",
  MovingTimeline = "movingTimeline",
  Pickmany = "pickmany",
  RefinanceReady = "refinanceReady",
  Start = "start",
  WillPayMore = "willPayMore",
}

export type FlowQuestionData = {
  description?: string
  inputConfig?: FlowInputConfig[] | FlowInputConfig
  layout?: FlowQuestionLayout
  maxAnswers?: number
  minAnswers?: number
  next?: FlowQuestionId
  nextCallback?: Function
  title: string
  type: FlowQuestionType
}

export interface FlowInputConfig {
  detail?: string
  id?: string
  isOther?: boolean
  pattern?: string
  placeholder?: string
  title?: string
}

export type FlowVocabularyData = Record<string, FlowDictionaryTerm>

export interface FlowDictionaryTerm {
  description: string
  title: string
  triggers: FlowDictionaryTermTriggers[]
}

export interface FlowDictionaryTermTriggers {
  id: string
  values: string[]
}

export interface FlowLearnMoreData {
  answerSpecificContent: Record<string, FlowAnswerSpecificContent>
  img?: ImageResponsive
  trigger: FlowQuestionId
  type: LearnMoreTileSize
}

export interface FlowAnswerSpecificContent {
  description: string
  link: string
  title: string
}

export enum LearnMoreTileSize {
  Large = "large",
  Medium = "medium",
  Small = "small",
}

export interface FlowOutcomes {
  options: Record<string, FlowOutcomeData>
  showSummary: boolean
  lead: FlowLeadData
}

export interface FlowOutcomeData {
  cta: string
  description: string
  img: HeroImageData
  title: string
  url: string
}

export enum FlowQuestionLayout {
  Grid = "grid",
  List = "list",
}

export type FlowUserAnswer = {
  display?: string
  value?: string | Array<{ id: string; value: string }>
}

export type FlowPickOneAnswer = {
  id: string
  title: string
  detail?: string
}

export type FlowInputCallbackArgs = {
  flowKey: FlowQuestionId
  value: string | Array<{ id: string; value: string }>
  display: string
}

export type FlowInputCallback = (args: FlowInputCallbackArgs) => void

export enum FlowInputTextType {
  Currency = "currency",
  Date = "date",
  Email = "email",
  FullDate = "full-date",
  Input = "input",
  Percentage = "percentage",
}

export enum FlowQuestionType {
  Currency = "currency",
  Date = "date",
  Email = "email",
  FullDate = "full-date",
  Percentage = "percentage",
  PickMany = "pick-many",
  PickOne = "pick-one",
}

export enum FlowSectionState {
  Active = "active",
  Next = "next",
  Prev = "prev",
}

export enum FlowExpanationVideoId {
  BetterRateRefinance = "better-rate-refinance",
  CashOutRefinance = "cash-out-refinance",
  SaveMoneyRefinance = "save-money-refinance",
}

export interface FlowVideos {
  trigger: FlowQuestionId
  answerSpecificContent: Record<FlowAnswer, FlowVideoData>
}

export interface FlowVideoData {
  videoId: FlowExpanationVideoId
}

export enum FlowAnswer {
  Arm = "arm",
  Cash = "cash",
  False = "false",
  Faster = "faster",
  High = "high",
  Low = "low",
  Lower = "lower",
  Medium = "medium",
  True = "true",
}

export interface FlowStartData {
  title: string
  description: string
}

export enum FlowOutcomeId {
  GuideArm = "guideArm",
  GuideFaster = "guideFaster",
  RefiArmCheap = "refiArmCheap",
  RefiArmLong = "refiArmLong",
  RefiArmShort = "refiArmShort",
  RefiCash = "refiCash",
  RefiFaster = "refiFaster",
  RefiLower = "refiLower",
}

export interface FlowLeadData {
  askForEmail: boolean
  description: string
  loanPurpose: string
  sourceSubtype: string
  title: string
}
